// StoreSidebar.tsx - Fixed version
import { Link, useLocation } from "react-router-dom";
import "./Sidbar.css";
import { useTranslation } from "react-i18next";
import { GoSidebarCollapse, GoSidebarExpand } from "react-icons/go";
import { IMAGES } from "../../../../assets/Images/Image";
import React, { JSX, useState, useEffect } from "react";
import { NavItem } from "../navItem/NavItem";
import { MenuItems, VRoutes } from "../../../../navigation/Routes";
import { requiredRoles } from "../../../../helpers/RequiredRoleHelpers";
import { useSelector } from "react-redux";
import { selectNewOrderCount } from "../../../../store/selectors/OrderSlice";
import { AccessManager } from "../../../../helpers/accessManager/AccessManager";
import { BiSupport } from "react-icons/bi";
import { COLORS } from "../../../../utils/Colors";

interface NavItemProps {
  isCollapsed?: boolean;
  onNavigate?: () => void;
  onToggleCollapse: () => void;
}

export const StoreSidebar: React.FC<NavItemProps> = ({
  onNavigate,
  isCollapsed,
  onToggleCollapse,
}) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const newOrderCount = useSelector(selectNewOrderCount);
  const [sidebarItems, setSidebarItems] = useState<JSX.Element[]>([]);

  const store = useSelector((state: any) => state.store);

  const toggleStats = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsStatsOpen(!isStatsOpen);
  };

  useEffect(() => {
    if (AccessManager.getInstance().isExpired()) {
      return;
    }
    const loadSidebarItems = async () => {
      const items = await Promise.all(
        MenuItems.map(async (item) => {
          const hasAccess = await requiredRoles(item.role);
          if (!hasAccess) return null;

          return (
            <NavItem
              key={item.path}
              path={item.path}
              icon={item.icon}
              label={t(`sidebar.${item.label}`)}
              isActive={pathname === item.path}
              isCollapsed={isCollapsed}
              notifCount={
                item.label === "orders" && newOrderCount > 0
                  ? newOrderCount
                  : undefined
              }
            />
          );
        }),
      );

      setSidebarItems(items.filter(Boolean) as JSX.Element[]);
    };

    loadSidebarItems();
  }, [pathname, isCollapsed, t, newOrderCount]);

  return (
    <div className={`mc-sidebar ${isCollapsed ? "collapsed" : ""}`}>
      <div className="mchic-header">
        {!isCollapsed && (
          <img src={IMAGES.LOGO} alt="Logo" className="mchic-logo" />
        )}
        {/* Toggle button - now positioned absolutely in the header */}
        <button
          className="collapse-toggle"
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <GoSidebarCollapse size={20} />
          ) : (
            <GoSidebarExpand size={20} />
          )}
        </button>
      </div>

      <div className="seperator" />

      <ul
        className={`sidebar-menu ${isCollapsed ? "collapsed-item-container" : ""}`}
      >
        {sidebarItems}
      </ul>

      {/** Support Section */}
      <div className="support-container">
        <Link
          to={`${VRoutes.DASHBOARD}/${VRoutes.SUPPORT_BY_CHAT}`}
          className={`support-link ${
            pathname === `${VRoutes.DASHBOARD}/${VRoutes.SUPPORT_BY_CHAT}`
              ? "active"
              : ""
          } ${isCollapsed ? "collapsed" : ""}`}
          title={isCollapsed ? t("sidebar.support") : undefined}
        >
          <div className="support-icon-wrap">
            <BiSupport size={22} />
          </div>
          {!isCollapsed && (
            <div className="support-text">
              <span className="support-label">{t("sidebar.support")}</span>
              <span className="support-subtext">
                {t("sidebar.supportSubtext")}
              </span>
            </div>
          )}
        </Link>
      </div>
    </div>
  );
};
